import {
  Button,
  Field,
  FormInput,
  FormRoot,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { Sparkles } from "lucide-react";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { statusOptions } from "../utils/labels";

type DepartmentItem = { id: string; name: string };

const FlowFormSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ Flow"),
  description: z.string().optional().nullable(),
  purpose: z.string().optional().nullable(),
  departmentId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  status: z.enum(["active", "draft", "inactive"]),
});
export type FlowFormValues = z.infer<typeof FlowFormSchema>;

export function FlowForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<FlowFormValues>;
  onSubmit: (values: FlowFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<FlowFormValues>({
    resolver: zodResolver(FlowFormSchema) as any,
    defaultValues: {
      title: "",
      description: null,
      purpose: null,
      departmentId: null,
      status: "draft",
      ...initialValues,
    },
  });

  // Check if in dev mode
  const isDevMode =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Generate random data for auto fill
  const generateRandomData = async () => {
    const titles = [
      "Flow ການຮັບສິນຄ້າ",
      "Flow ການຈັດສົ່ງ",
      "Flow ການບັນຍັດສິນຄ້າ",
      "Flow ການກວດສອບຄຸນນະພາບ",
      "Flow ການຈັດການສາງ",
      "Flow ການຂາຍ",
      "Flow ການບໍລິການລູກຄ້າ",
      "Flow ການຈັດການສິນຄ້າຄົງຄັງ",
    ];

    const descriptions = [
      "ຂະບວນການຮັບສິນຄ້າເຂົ້າສາງ",
      "ຂະບວນການຈັດສົ່ງສິນຄ້າໄປຍັງລູກຄ້າ",
      "ຂະບວນການບັນຍັດສິນຄ້າຕາມຄຳສັ່ງຊື້",
      "ຂະບວນການກວດສອບຄຸນນະພາບສິນຄ້າ",
      "ຂະບວນການຈັດການສາງສິນຄ້າ",
      "ຂະບວນການຂາຍສິນຄ້າ",
      "ຂະບວນການບໍລິການລູກຄ້າ",
      "ຂະບວນການຈັດການສິນຄ້າຄົງຄັງ",
    ];

    const purposes = [
      "ເພື່ອຮັບປະກັນຄຸນນະພາບຂອງສິນຄ້າ",
      "ເພື່ອເພີ່ມປະສິດທິພາບການດຳເນີນງານ",
      "ເພື່ອປັບປຸງປະສົບການລູກຄ້າ",
      "ເພື່ອຫຼຸດຜ່ອນຂໍ້ຜິດພາດ",
      "ເພື່ອຮັບປະກັນຄວາມປອດໄພ",
      "ເພື່ອຈັດການສິນຄ້າໃຫ້ມີປະສິດທິພາບ",
    ];

    // Get first department for departmentId
    let departmentId: string | null = null;
    try {
      const departmentsResult = await fetchLookupForInfinite<DepartmentItem>(
        `${config.apiUrl}/flows/departments/lookup`,
        { search: "", pageParam: 0 },
      );
      const firstDepartment = departmentsResult.items?.[0];
      if (firstDepartment?.id) {
        departmentId = firstDepartment.id;
      }
    } catch {
      // If fetching departments fails, leave departmentId null (user can select manually)
    }

    const randomTitle = titles[Math.floor(Math.random() * titles.length)] ?? "";
    const randomDescription =
      descriptions[Math.floor(Math.random() * descriptions.length)] ?? null;
    const randomPurpose =
      purposes[Math.floor(Math.random() * purposes.length)] ?? null;

    // Set form values
    methods.setValue("title", randomTitle);
    if (randomDescription) {
      methods.setValue("description", randomDescription);
    }
    if (randomPurpose) {
      methods.setValue("purpose", randomPurpose);
    }
    if (departmentId) {
      methods.setValue("departmentId", departmentId);
    }
    methods.setValue(
      "status",
      (["active", "draft", "inactive"][Math.floor(Math.random() * 3)] as
        | "active"
        | "draft"
        | "inactive") ?? "draft",
    );

    toast.success("Auto-filled form with random data");
  };

  return (
    <FormRoot<FlowFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInput
        name="title"
        label="ຊື່ Flow"
        requiredMark
        placeholder="ກະລຸນາໃສ່ຊື່ Flow"
      />
      <FormInput
        name="description"
        label="ຄຳອະທິບາຍ"
        placeholder="ຄຳອະທິບາຍ Flow"
      />
      <FormInput name="purpose" label="ຈຸດປະສົງ" placeholder="ຈຸດປະສົງຂອງ Flow" />
      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        queryKey={["flows-departments"]}
        queryFn={(args) =>
          fetchLookupForInfinite(
            `${config.apiUrl}/flows/departments/lookup`,
            args,
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/flows/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
        clearable
      />
      <Field name="status" label="ສະຖານະ" requiredMark>
        <RHF.Controller
          control={methods.control}
          name="status"
          render={({ field }) => (
            <SimpleSelect
              value={field.value}
              onValueChange={field.onChange}
              options={statusOptions}
              placeholder="ເລືອກສະຖານະ"
            />
          )}
        />
      </Field>

      <div className="flex items-center justify-between pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateRandomData}
            className="text-xs"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            Auto Fill (Dev)
          </Button>
        )}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
